/**
 * The make-up air unit's dialog - the HVAC technician's bench.
 *
 * Six knobs, all of them things a technician really turns or the weather
 * really sets, and the unit's loads read straight off the psychrometrics
 * in sim/facilities/MauSim. Live rather than frozen at open, unlike the
 * skid dialogs, because this one exists to be tinkered with: drag the
 * outside air from winter to summer and watch the steam fall away as the
 * cooling coil picks up, then the reheat come on beside it.
 */

import { Modal } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import {
  MAU_DESIGN,
  MAU_LIMITS,
  MAU_WEATHER,
  type MauSettings,
  type MauWarning,
} from '../../sim/facilities/MauSim'
import { mauStore } from '../../sim/facilities/mauStore'
import { repairOutcome, skidReadings, type HouseBus } from '../../sim/house/model'
import type { Journal } from '../../learn/Journal'
import { KnobBank, type KnobLimit } from '../../ui/kit/controls'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'

const WARNING_TEXT: Record<MauWarning, [string, string]> = {
  coilFreeze: ['WarnFreeze', 'The chilled-water coil is being asked for a dew point near freezing: frost risk.'],
  coolingCapacity: ['WarnCooling', 'The cooling coil is past its rated capacity; the supply will run warm and wet.'],
  heatingCapacity: ['WarnHeating', 'The hot-water coils are past their rated capacity; the supply will run cold.'],
  steamCapacity: ['WarnSteam', 'The humidifier is past its rated capacity; the cleanroom will dry out.'],
  pressureLow: ['WarnPressureLow', 'Cleanroom pressurisation lost: the corridor is breathing in through the cracks.'],
  pressureHigh: ['WarnPressureHigh', 'Over-pressurised: doors will fight their closers and the FFU plenum will lift.'],
}

const DOT = { good: '#4fae6d', warning: '#d8a020', critical: '#e0564a' } as const

export function MauDialog({ journal, house, onClose }: { journal: Journal; house: HouseBus; onClose: () => void }) {
  useLang()
  const [, bump] = useState(0)
  useEffect(() => mauStore.subscribe(() => bump((n) => n + 1)), [])
  // The unit's gauges and its one repair, frozen at open like every skid's:
  // the question is what you found when you walked up.
  const readings = useMemo(() => skidReadings('mau', house.state), [house])
  const outcome = useMemo(() => repairOutcome('mau', house.armed), [house])
  const [verdict, setVerdict] = useState<{ message: string; cleared: boolean } | null>(null)
  const repair = (): void => {
    journal.record({
      kind: 'maintenance',
      action: 'house:mau',
      label: t('house.system.mau.Label'),
      hours: outcome.hours,
      tool: 'FAC-06',
    })
    const hadEvent = house.armed !== null
    if (outcome.clears) house.arm(null)
    const message = outcome.clears
      ? tOr('subfab.RepairCleared', 'Repair complete and the loop re-qualified. The utility board is green again — walk upstairs and watch the tools recover.')
      : hadEvent
        ? tOr('subfab.RepairWasted', 'Repaired, re-certified… and the symptom upstairs has not moved. This plant was healthy; the hours are spent.')
        : tOr('subfab.RepairIdle', 'Nothing was wrong with this plant. Preventive work, logged and charged.')
    setVerdict({ message, cleared: outcome.clears })
  }
  const s = mauStore.settings
  const r = mauStore.report
  const L = (key: string, fallback: string) => tOr(`subfab.mau.${key}`, fallback)

  const rows: Array<[string, string, string]> = [
    ['Flow', 'Supply / exhaust', `${r.supplyM3h.toFixed(0)} / ${r.exhaustM3h.toFixed(0)} m³/h`],
    ['Moisture', 'Moisture in / out', `${(r.outsideW * 1000).toFixed(1)} → ${(r.supplyW * 1000).toFixed(1)} g/kg`],
    ['DewPoint', 'Supply dew point', `${r.supplyDewPointC.toFixed(1)} °C`],
    ['CoilLeaving', 'Cooling coil leaves at', `${r.coilLeavingC.toFixed(1)} °C${r.dehumidifying ? ` · ${L('Dehumidifying', 'dehumidifying')}` : ''}`],
    ['Cooling', 'Cooling coil', `${r.coolingKw.toFixed(0)} kW / ${MAU_DESIGN.coolingKw}`],
    ['Heating', 'Preheat + reheat coils', `${r.heatingKw.toFixed(0)} kW / ${MAU_DESIGN.heatingKw}`],
    ['Steam', 'Humidifier steam', `${r.steamKgH.toFixed(0)} kg/h / ${MAU_DESIGN.steamKgH}`],
    ['FanPower', 'Fan power', `${r.fanKw.toFixed(1)} kW`],
    ['Power', 'Power, chiller at its COP', `${r.powerKw.toFixed(0)} kW`],
    ['Pressure', 'Cleanroom pressure', `${r.cleanroomPa >= 0 ? '+' : ''}${r.cleanroomPa.toFixed(1)} Pa`],
  ]

  return (
    <Modal open onCancel={onClose} footer={null} title={L('Title', 'Make-up air unit MAU-1')} width="min(760px, 96vw)">
      <div className="modal-body" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div style={{ marginBottom: 10 }}>
            {L(
              'Intro',
              'Outside air in, cleanroom air out. Dehumidifying means cooling to the dew point and ' +
                'reheating; winter means steam; pressure is what is left after the exhaust.',
            )}
          </div>
          <div className="btn-row" style={{ marginBottom: 8 }}>
            {(Object.keys(MAU_WEATHER) as Array<keyof typeof MAU_WEATHER>).map((key) => (
              <button key={key} className="btn" onClick={() => mauStore.set(MAU_WEATHER[key])}>
                {L(`Weather${key[0].toUpperCase()}${key.slice(1)}`, { summer: 'Summer design day', winter: 'Winter design day', mild: 'Mild day' }[key])}
              </button>
            ))}
            <button className="btn" onClick={() => mauStore.reset()}>
              {L('Reset', 'Reset')}
            </button>
          </div>
          <KnobBank<MauSettings>
            recipe={s}
            limits={MAU_LIMITS as unknown as Record<keyof MauSettings, KnobLimit>}
            order={['outsideC', 'outsideRh', 'supplyC', 'supplyRh', 'fanPct', 'exhaustPct']}
            onChange={(patch) => mauStore.set(patch)}
            labelFor={(key) => L(`Knob${String(key)[0].toUpperCase()}${String(key).slice(1)}`, MAU_LIMITS[key].label)}
          />
        </div>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          {rows.map(([key, fallback, value]) => (
            <div key={key} style={{ display: 'flex', gap: 9, margin: '6px 0', alignItems: 'baseline' }}>
              <span style={{ flex: 1 }}>{L(key, fallback)}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
            </div>
          ))}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hair)' }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-2)', marginBottom: 4 }}>{L('Readings', 'On the unit')}</div>
            {readings.map((rd) => (
              <div key={rd.id} style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '5px 0' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', flex: 'none', background: DOT[rd.state] }} />
                <span style={{ flex: 1 }}>{tOr(`subfab.reading.${rd.id}`, rd.label)}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rd.value}</span>
              </div>
            ))}
            {verdict ? (
              <div style={{ marginTop: 8, fontSize: '12.5px', lineHeight: 1.55, color: verdict.cleared ? DOT.good : DOT.warning }}>{verdict.message}</div>
            ) : (
              <button className="btn primary" style={{ marginTop: 8 }} onClick={repair}>
                {tOr('house.system.mau.Action', outcome.action).replace(/^[^:：]*[:：]\s*/, '')} · {outcome.hours} h
              </button>
            )}
          </div>
          {r.warnings.length > 0 && (
            <div style={{ marginTop: 10, color: '#d8a020', fontSize: '12.5px', lineHeight: 1.5 }}>
              {r.warnings.map((w) => (
                <div key={w}>⚠ {L(WARNING_TEXT[w][0], WARNING_TEXT[w][1])}</div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: '12px', color: 'var(--ink-2)', lineHeight: 1.5 }}>
            {L(
              'Note',
              'Rating plate: 12 000 m³/h, 240 kW cooling, 130 kW heating, 110 kg/h steam. The cleanroom ' +
                'pressure this unit holds is what every bay upstairs pressurises against.',
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
